import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import { Tv, Bus, Monitor, Film, Clapperboard, Radio, ArrowUpRight, Check } from "lucide-react";
import SectionHeading from "@/components/shared/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_SERVICES = [
  {
    id: "billboards",
    title: "Billboards",
    subtitle: "Static Outdoor Dominance",
    description: "Premium roadside, highway, and high-density urban billboards. Static prints in trophy locations engineered for maximum dwell-time and recall.",
    highlights: ["Trophy positions", "Premium illumination", "Highway dominance"],
  },
  {
    id: "digital_hoardings",
    title: "Digital Hoardings",
    subtitle: "Premium LED · Cinematic Motion · Cityscape Dominance",
    description: "Full-motion digital LED hoardings and programmatic DOOH screens with real-time creative swaps, dayparting, and live audience analytics.",
    highlights: ["Premium LED clusters", "Cinematic motion quality", "Cityscape dominance"],
  },
  {
    id: "transit",
    title: "Transit Advertising",
    subtitle: "Buses, Metros, Autos",
    description: "Reach commuters where they live and move. Wraps, interior panels, and station takeovers across major transit networks.",
    highlights: ["Daily commuter reach", "Full vehicle wraps", "Station dominations"],
  },
  {
    id: "cinema",
    title: "Cinema Advertising",
    subtitle: "Captive Audience, Big Screen",
    description: "On-screen pre-show advertising in premium multiplexes. The most attentive audience in any media plan, delivered in cinematic quality.",
    highlights: ["Premium multiplexes", "Captive viewers", "Big-screen impact"],
  },
  {
    id: "film",
    title: "Film Advertising",
    subtitle: "Brand Integration in Movies",
    description: "In-film product placement, brand integration, and bespoke co-marketing tie-ups with leading studios and production houses.",
    highlights: ["Product placement", "Brand integration", "Studio partnerships"],
  },
  {
    id: "radio",
    title: "Radio Channel Advertising",
    subtitle: "Audio That Travels",
    description: "Hyperlocal city-wide radio spots and sponsored segments across leading FM channels. Built for frequency, drive-time reach, and recall.",
    highlights: ["Drive-time slots", "Sponsored segments", "City-wide frequency"],
  },
];

const ICONS = {
  billboards: Tv,
  digital_hoardings: Monitor,
  transit: Bus,
  cinema: Film,
  film: Clapperboard,
  radio: Radio,
};
const IMAGES = {
  billboards: "https://images.unsplash.com/photo-1604928593934-3ba7b68219ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  digital_hoardings: "https://images.unsplash.com/photo-1708107243374-52eddcc3fe64?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  transit: "https://images.pexels.com/photos/16973293/pexels-photo-16973293.jpeg?auto=compress&cs=tinysrgb&w=1400",
  cinema: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  film: "https://images.unsplash.com/photo-1485846234645-a62644f84728?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  radio: "https://images.unsplash.com/photo-1485579149621-3123dd979885?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

export default function Services() {
  const [services, setServices] = useState(FALLBACK_SERVICES);

  useEffect(() => {
    axios
      .get(`${API}/services`)
      .then((r) => {
        if (r.data?.services?.length) setServices(r.data.services);
      })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="services-page" className="text-zinc-100">
      <section className="relative pt-36 md:pt-44 pb-20 border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-950/30 via-zinc-950 to-zinc-950" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <div className="font-mono-accent text-[11px] text-amber-400 mb-5">Services</div>
          <h1 className="font-display text-5xl md:text-7xl font-black leading-[1] tracking-tighter max-w-4xl">
            Six OOH formats.
            <br />
            <span className="gradient-text italic">One ultimate advantage.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-zinc-400 leading-relaxed">
            Whether you're launching, scaling, or owning a moment, we have a
            format engineered for the goal.
          </p>
        </div>
      </section>

      <section className="bg-zinc-950">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 md:py-28 space-y-20">
          {services.map((s, i) => {
            const Icon = ICONS[s.id] || Tv;
            const reverse = i % 2 === 1;
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className={`grid grid-cols-1 md:grid-cols-12 gap-8 items-center ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}
                data-testid={`service-section-${s.id}`}
              >
                <div className="md:col-span-7">
                  <div className="relative overflow-hidden rounded-3xl border border-white/5 group">
                    <img
                      src={IMAGES[s.id]}
                      alt={s.title}
                      className="h-[420px] md:h-[520px] w-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-1000"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950 via-transparent to-transparent" />
                    <div className="absolute bottom-6 left-6 font-mono-accent text-[10px] text-zinc-300 bg-black/40 backdrop-blur px-3 py-1.5 rounded-full border border-white/10">
                      0{i + 1} / 0{services.length}
                    </div>
                  </div>
                </div>
                <div className="md:col-span-5">
                  <Icon className="h-9 w-9 text-blue-400" />
                  <div className="mt-4 font-mono-accent text-[10px] text-zinc-500">{s.subtitle}</div>
                  <h2 className="mt-3 font-display text-4xl md:text-5xl font-black tracking-tighter">
                    {s.title}
                  </h2>
                  <p className="mt-5 text-zinc-300 leading-relaxed text-base md:text-lg">
                    {s.description}
                  </p>
                  <ul className="mt-7 space-y-3">
                    {(s.highlights || []).map((h) => (
                      <li key={h} className="flex items-start gap-3 text-zinc-300">
                        <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-500/15 text-blue-400">
                          <Check className="h-3 w-3" />
                        </span>
                        {h}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid={`service-cta-${s.id}`}
                    className="mt-9 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold hover:bg-white/10 transition-all"
                  >
                    Plan a {s.title} campaign <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="bg-zinc-950 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24">
          <SectionHeading
            overline="Not sure which format?"
            title="We'll build the plan for you."
            subtitle="Share your goal, audience, and budget. We'll come back with a recommended media mix in 24 hours."
          />
          <Link
            to="/contact"
            data-testid="services-bottom-cta"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-white text-zinc-950 px-7 py-4 text-sm font-bold hover:bg-amber-300 transition-all"
          >
            Request a Media Plan <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

